'use client';

import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { format, isWithinInterval } from 'date-fns';
import { MdArrowOutward } from 'react-icons/md';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@components/common/Dialog';
import { EventsLists } from './eventsLists';
import { EventsCalendar } from './eventsCalendar';
import { parseDate } from '@utils/dateUtils';
import useUpcomingEventsStore from '@stores/contentStore/upcomingEventsStore';
import { InterviewScheduleSkeleton } from '@components/loaders/SkeletonLoaders';

function UpcomingEvents() {
    const { upcomingEventsData, fetchUpcomingEventsData, clearStore, loadingUpcomingEventsData } =
        useUpcomingEventsStore();

    useEffect(() => {
        fetchUpcomingEventsData();
        return clearStore;
    }, [fetchUpcomingEventsData, clearStore]);

    const [dateRange, setDateRange] = useState();
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);

    const filteredEvents = useMemo(() => {
        return upcomingEventsData.filter(event => {
            const matchesSearch = event.EventName.toLowerCase().includes(search.toLowerCase());

            const candidateDate = parseDate(event.EventDate);

            if (!candidateDate) return false;

            const matchesDateRange =
                !dateRange?.from ||
                !dateRange?.to ||
                isWithinInterval(candidateDate, {
                    start: new Date(dateRange.from.setHours(0, 0, 0, 0)),
                    end: new Date(dateRange.to.setHours(23, 59, 59, 999)),
                });

            return matchesSearch && matchesDateRange;
        });
    }, [upcomingEventsData, search, dateRange]);

    const interviewDates = useMemo(() => {
        const dates = new Map();
        upcomingEventsData.forEach(candidate => {
            const candidateDate = parseDate(candidate.EventDate);
            if (candidateDate) {
                const date = format(candidateDate, 'yyyy-MM-dd');
                if (!dates.has(date)) dates.set(date, []);
                dates.get(date).push(candidate);
            }
        });
        return dates;
    }, [upcomingEventsData]);

    const clearFilters = () => {
        setDateRange(undefined);
        setSearch('');
    };

    return (
        <Dialog className="z-[99999]" open={open} onOpenChange={() => setOpen(!open)}>
            <div className="w-full col-span-2  p-4 rounded-[10px] bg-white mb-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-lg font-bold">Upcoming Events:</div>
                    <DialogTrigger>
                        <div className="flex items-center justify-center gap-2 text-sm text-primary-blue hover:text-blue-800 font-semibold cursor-pointer">
                            Show all <MdArrowOutward />
                        </div>
                    </DialogTrigger>
                </div>

                <DialogContent fullscreen={true} className="bg-white h-[calc(100vh-100px)]">
                    <DialogHeader>
                        <DialogTitle>Upcoming Events:</DialogTitle>
                    </DialogHeader>
                    <div className="h-full flex lg:flex-row flex-col items-start gap-2 relative box-border transition-all duration-300">
                        <div className="lg:w-7/12 w-full">
                            <EventsLists
                                filteredEvents={filteredEvents}
                                search={search}
                                setSearch={setSearch}
                                dateRange={dateRange}
                                clearFilters={clearFilters}
                            />
                        </div>
                        <div className="lg:w-5/12 w-full">
                            <EventsCalendar
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                interviewDates={interviewDates}
                            />
                        </div>
                    </div>
                </DialogContent>

                {loadingUpcomingEventsData && <InterviewScheduleSkeleton />}

                {!loadingUpcomingEventsData && (
                    <div className="h-full flex lg:flex-row flex-col items-start gap-2 relative box-border transition-all duration-300">
                        <div className="lg:w-7/12 w-full">
                            <EventsLists
                                filteredEvents={filteredEvents}
                                search={search}
                                setSearch={setSearch}
                                dateRange={dateRange}
                                clearFilters={clearFilters}
                            />
                        </div>
                        <div className="lg:w-5/12 w-full">
                            <EventsCalendar
                                dateRange={dateRange}
                                setDateRange={setDateRange}
                                interviewDates={interviewDates}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}

UpcomingEvents.propTypes = {
    upcomingEventsData: PropTypes.array,
};

export default UpcomingEvents;
